import React, { useEffect } from 'react';
import { SlPaypal } from 'react-icons/sl';
import { VscCreditCard } from 'react-icons/vsc';
import { getTranslationKey } from '../../modules/utils';
import styled from 'styled-components';
import { Alert, Button, Col, Row, UncontrolledTooltip } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { obfuscateString, Title, useUserHasPayinPaymentMethod } from './common';
import { PayoutMethodCardForm } from './PayoutMethodCardForm';
import { fetchPayoutAccountAction } from '../../modules/actions';
import { InputItem } from '../../components';
import { usePayoutMethods } from './payout-methods';
import { CardTitle } from './DisplayCard';
import { getPayoutAccountSelector } from '../../modules/selectors';
import { usePaymentPrerequisites } from './use-payment-prerequisites';
import { media } from '../../assets/css/media';

const ViewWrapper = styled.div<{ background: string }>`
  position: relative;
  font-weight: 300;
  font-size: 1rem;
  color: #1d1d1d;
  div {
    white-space: nowrap;
    overflow: hidden;
  }
  &:after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    background: linear-gradient(to left, ${(props) => props.background} 0px, transparent 10%);
  }
`;

const InputWrapper = styled.div`
  margin: 10px 0 10px 0;
`;

const Error = styled.span`
  color: #f86c6b;
`;

const CardTitleWrap = styled(CardTitle)`
  flex-direction: column;
  align-items: flex-start;
`;

const TitleWrap = styled.div`
  display: flex;
  align-items: center;
`;

const Subtitle = styled.span`
  @media all and (max-width: ${media.mobile}) {
    font-size: 12px !important;
  }
`;

const TitleItem = styled.span`
  @media all and (max-width: ${media.mobile}) {
    font-size: 15px !important;
  }
`;

const DesktopOnlyButton = styled.div`
  @media all and (max-width: ${media.desktop_min}) {
    display: none;
  }
`;

const MobileOnlyButton = styled.div`
  display: none;
  @media all and (max-width: ${media.desktop_min}) {
    display: block;
  }
`;

export const PayoutMethodsTab = () => {
  const dispatch = useDispatch();
  const payoutAccount = useSelector(getPayoutAccountSelector);
  const payoutMethods = usePayoutMethods();

  const userHasPaymentMethod = useUserHasPayinPaymentMethod();

  const prerequisites = usePaymentPrerequisites();
  const allPrerequisitesMet = prerequisites.every((p) => p.isMet);
  const isCustomerPaymentTabAvailable = allPrerequisitesMet && userHasPaymentMethod;

  useEffect(() => {
    dispatch(fetchPayoutAccountAction());
  }, []);

  const submitForm = (e: React.SyntheticEvent) => {
    e.preventDefault();
    payoutMethods.submit();
  };

  const handleFocus = (event: React.BaseSyntheticEvent) => event.target.select();

  const submitButton = (
    <Button
      color="primary"
      className="px-5 mb-4 text-uppercase"
      disabled={
        !(payoutMethods.paypal.submissionAllowed() || payoutMethods.iban.submissionAllowed()) ||
        payoutMethods.submitting
      }
      type="submit"
      onClick={submitForm}
    >
      {getTranslationKey('save')}
    </Button>
  );

  return (
    <div>
      <Title className="mb-4">{getTranslationKey('shop.paymentSettings.payoutMethodsTitle')}</Title>

      {isCustomerPaymentTabAvailable ? (
        <>
          <div
            className="mb-5"
            dangerouslySetInnerHTML={{
              __html: getTranslationKey('shop.paymentSettings.payoutMethodsInfo')
            }}
          />

          {payoutAccount.loaded && (
            <Row className="animated fadeIn">
              <Col md={6}>
                <PayoutMethodCardForm
                  enabled={payoutMethods.paypal.local.payPalEnabled}
                  focused={payoutMethods.paypal.ui.focused}
                  toggle={() => {
                    payoutMethods.paypal.enable();
                  }}
                  enablingDisabled={!payoutMethods.paypal.enablingAllowed()}
                  editModeCanBeClosed={
                    !!payoutMethods.paypal.remote.payPalClientId &&
                    !!payoutMethods.paypal.remote.payPalSecret
                  }
                  edit={payoutMethods.paypal.ui.edit}
                  setEdit={payoutMethods.paypal.toggleEditMode}
                  title={
                    <CardTitleWrap>
                      <TitleWrap>
                        <SlPaypal size={22} color="#1d1d1d" className="mr-2" />
                        <TitleItem className="title mr-1">PayPal</TitleItem>
                        <i className="fa fa-icon fa-question-circle" id="paypal-transfer-tooltip" />
                        <UncontrolledTooltip
                          autohide={false}
                          placement="top"
                          target="paypal-transfer-tooltip"
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html: getTranslationKey('shop.settings.paypalTransferTooltip')
                            }}
                          />
                        </UncontrolledTooltip>
                      </TitleWrap>
                      <Subtitle className="subtitle">
                        {getTranslationKey('shop.paymentSettings.recommended')}
                      </Subtitle>
                    </CardTitleWrap>
                  }
                  form={
                    <>
                      <InputWrapper>
                        <InputItem
                          id="paypal-client-id"
                          label={getTranslationKey('shop.settings.payPalClientId')}
                          type="text"
                          changeCallback={(v) =>
                            payoutMethods.paypal.changeField('payPalClientId', v)
                          }
                          value={String(payoutMethods.paypal.local.payPalClientId)}
                          placeholder={getTranslationKey('shop.settings.payPalClientId')}
                          withTooltip
                          tooltipText={getTranslationKey('shop.settings.payPalClientIdTooltip')}
                          tooltipIconPlacement="label"
                          onFocus={(e) => {
                            handleFocus(e);
                            payoutMethods.paypal.setFocused(true);
                          }}
                          onBlurCallback={() => {
                            payoutMethods.paypal.setFocused(false);
                          }}
                        />
                        {payoutMethods.paypal.ui.showErrors &&
                          payoutMethods.paypal.validation.payPalClientId && (
                            <Error>{payoutMethods.paypal.validation.payPalClientId}</Error>
                          )}
                      </InputWrapper>
                      <InputWrapper>
                        <InputItem
                          id="paypal-client-secret"
                          label={getTranslationKey('shop.settings.payPalSecret')}
                          type="text"
                          changeCallback={(v) =>
                            payoutMethods.paypal.changeField('payPalSecret', v)
                          }
                          value={String(payoutMethods.paypal.local.payPalSecret)}
                          placeholder={getTranslationKey('shop.settings.payPalSecret')}
                          withTooltip
                          tooltipText={getTranslationKey('shop.settings.payPalSecretTooltip')}
                          tooltipIconPlacement="label"
                          onFocus={(e) => {
                            handleFocus(e);
                            payoutMethods.paypal.setFocused(true);
                          }}
                          onBlurCallback={() => {
                            payoutMethods.paypal.setFocused(false);
                          }}
                        />
                        {payoutMethods.paypal.ui.showErrors &&
                          payoutMethods.paypal.validation.payPalSecret && (
                            <Error>{payoutMethods.paypal.validation.payPalSecret}</Error>
                          )}
                      </InputWrapper>
                    </>
                  }
                  view={({ background }) => (
                    <ViewWrapper background={background}>
                      <div>{payoutMethods.paypal.remote.payPalClientId}</div>
                      <div>{payoutMethods.paypal.remote.payPalSecret}</div>
                    </ViewWrapper>
                  )}
                />
                <DesktopOnlyButton>
                  {payoutMethods.paypal.ui.edit && submitButton}
                </DesktopOnlyButton>
              </Col>
              <Col md={6}>
                <PayoutMethodCardForm
                  enabled={payoutMethods.iban.local.ibanEnabled}
                  focused={payoutMethods.paypal.ui.focused}
                  toggle={() => {
                    payoutMethods.iban.enable();
                  }}
                  enablingDisabled={!payoutMethods.iban.enablingAllowed()}
                  editModeCanBeClosed={
                    !!payoutMethods.iban.remote.accountHolder && !!payoutMethods.iban.remote.iban
                  }
                  edit={payoutMethods.iban.ui.edit}
                  setEdit={payoutMethods.iban.toggleEditMode}
                  title={
                    <CardTitleWrap>
                      <TitleWrap>
                        <VscCreditCard size={24} color="#1d1d1d" className="mr-2" />
                        <TitleItem className="title mr-1">
                          {getTranslationKey('shop.settings.bankTransfer')}
                        </TitleItem>
                        <i className="fa fa-icon fa-question-circle" id="bank-transfer-tooltip" />
                      </TitleWrap>
                      <UncontrolledTooltip
                        autohide={false}
                        placement="top"
                        target="bank-transfer-tooltip"
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: getTranslationKey('shop.settings.bankTransferTooltip')
                          }}
                        />
                      </UncontrolledTooltip>
                      <Subtitle className="subtitle">
                        {getTranslationKey('shop.paymentSettings.ibanRecommended')}
                      </Subtitle>
                    </CardTitleWrap>
                  }
                  form={
                    <>
                      <InputWrapper>
                        <InputItem
                          id="account-holder"
                          label={getTranslationKey('shop.settings.accountHolder')}
                          type="text"
                          changeCallback={(v) => payoutMethods.iban.changeField('accountHolder', v)}
                          value={String(payoutMethods.iban.local.accountHolder)}
                          placeholder={getTranslationKey('shop.settings.accountHolder')}
                          onFocus={(e) => {
                            handleFocus(e);
                            payoutMethods.iban.setFocused(true);
                          }}
                          onBlurCallback={() => {
                            payoutMethods.iban.setFocused(false);
                          }}
                        />
                        {payoutMethods.iban.ui.showErrors &&
                          payoutMethods.iban.validation.accountHolder && (
                            <Error>{payoutMethods.iban.validation.accountHolder}</Error>
                          )}
                      </InputWrapper>
                      <InputWrapper>
                        <InputItem
                          id="iban"
                          label={getTranslationKey('shop.settings.ibanInputLabel')}
                          type="text"
                          changeCallback={(v) => payoutMethods.iban.changeField('iban', v)}
                          value={String(payoutMethods.iban.local.iban)}
                          placeholder={getTranslationKey('shop.settings.ibanInputLabel')}
                          onFocus={(e) => {
                            handleFocus(e);
                            payoutMethods.iban.setFocused(true);
                          }}
                          onBlurCallback={() => {
                            payoutMethods.iban.setFocused(false);
                          }}
                        />
                        {payoutMethods.iban.ui.showErrors && payoutMethods.iban.validation.iban && (
                          <Error>{payoutMethods.iban.validation.iban}</Error>
                        )}
                      </InputWrapper>
                    </>
                  }
                  view={({ background }) => (
                    <ViewWrapper background={background}>
                      <div>{payoutMethods.iban.remote.accountHolder}</div>
                      <div>{obfuscateString(payoutMethods.iban.remote.iban)}</div>
                    </ViewWrapper>
                  )}
                />
                <DesktopOnlyButton>
                  {payoutMethods.iban.ui.edit && !payoutMethods.paypal.ui.edit && submitButton}
                </DesktopOnlyButton>
              </Col>
              <Col xs={12}>
                <MobileOnlyButton>
                  {(payoutMethods.paypal.ui.edit || payoutMethods.iban.ui.edit) && submitButton}
                </MobileOnlyButton>
              </Col>
            </Row>
          )}
        </>
      ) : (
        <Alert color="warning">
          {getTranslationKey('shop.settings.enterBillingAddressNotify')}
        </Alert>
      )}
    </div>
  );
};
